import React from 'react';
import { useHistory } from 'react-router-dom';
import alertActions from 'ducks/alert/actions';
import { alertTypesConstants } from 'commons/constants';
import { useDispatch } from 'react-redux';
import {
  LearnerHomeContainer,
  StyledColVertical,
  StyledRow,
  StyledRowTight,
} from './LearnerHome.styles';
import CoursesCarousel from './CoursesCarousel';
import PathwaysCarousel from './PathwaysCarousel';
import ProfileCard from './ProfileCard';
import UpdatesCard from './UpdatesCard';

const simulateApi = (shouldFail = false, delay = 1200) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (shouldFail) {
        reject();
      } else {
        resolve('data');
      }
    }, delay);
  });

type SectionKeys = 'profile' | 'courses' | 'pathways' | 'updates';

type LoadingState = Record<SectionKeys, boolean>;
type ErrorState = Record<SectionKeys, boolean>;

const sections = [
  { key: 'profile' as SectionKeys, delay: 100000 },
  { key: 'courses' as SectionKeys, delay: 100000 },
  { key: 'pathways' as SectionKeys, delay: 100000 },
  { key: 'updates' as SectionKeys, delay: 100000 },
];

const LearnerHome: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = React.useState<LoadingState>({
    profile: true,
    courses: true,
    pathways: true,
    updates: true,
  });
  const [error, setError] = React.useState<ErrorState>({
    profile: false,
    courses: false,
    pathways: false,
    updates: false,
  });

  React.useEffect(() => {
    sections.forEach(({ key, delay }) => {
      simulateApi(false, delay)
        .then(() => setLoading((l) => ({ ...l, [key]: false })))
        .catch(() => setError((e) => ({ ...e, [key]: true })));
    });
  }, []);

  React.useEffect(() => {
    if (Object.values(error).some(Boolean)) {
      dispatch(
        alertActions.setAlert({
          message: 'Error loading personalized dashboard. Redirecting to standard view.',
          type: alertTypesConstants.DANGER,
        }),
      );
      history.push('/');
    }
  }, [error, history]);

  return (
    <LearnerHomeContainer>
      <StyledRow gutter={[16, 16]}>
        <StyledColVertical
          xs={{ span: 24, order: 2 }}
          md={{ span: 24, order: 2 }}
          lg={{ span: 8, order: 1 }}
        >
          <UpdatesCard loading={loading.updates} updates={[]} />
        </StyledColVertical>
        <StyledColVertical
          xs={{ span: 24, order: 1 }}
          md={{ span: 24, order: 1 }}
          lg={{ span: 16, order: 2 }}
        >
          <ProfileCard loading={loading.profile} />
        </StyledColVertical>
      </StyledRow>

      <StyledRowTight gutter={[5, 5]}>
        <CoursesCarousel loading={loading.courses} courses={[]} />
      </StyledRowTight>

      <StyledRow gutter={[5, 5]}>
        <PathwaysCarousel loading={loading.pathways} pathways={[]} />
      </StyledRow>
    </LearnerHomeContainer>
  );
};

export default LearnerHome;
